import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import {
  Calendar,
  MapPin,
  Users,
  Package,
  Plus,
  Edit,
  Trash2,
  Eye,
  BarChart3,
  DollarSign,
  TrendingUp,
  MoreVertical,
  Copy,
  Search,
  Filter,
  ArrowUpDown,
  CheckCircle,
  AlertCircle,
  Clock
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { formatPrice } from '../../lib/square'
import { getEventStatus, getStatusColor } from '../../lib/utils/eventStatus'
import LoadingSpinner from '../ui/LoadingSpinner'
import toast from 'react-hot-toast'
import { EnterpriseToast } from '../ui/EnterpriseToast'

interface EventWithStats {
  id: string
  title: string
  description: string
  event_date: string
  location_name: string
  address: string
  max_capacity: number | null
  max_orders: number | null
  current_orders: number
  pickup_start_time: string
  pickup_end_time: string
  status: string
  created_at: string

  // Computed stats
  totalOrders: number
  totalRevenue: number
  averageOrderValue: number
  completionRate: number
  registeredUsers: number

  // Phase 3: Lobster tail capacity
  max_lobster_tails: number | null
  current_tail_count?: number
  capacity_percent?: number
  capacity_loading?: boolean
}

type SortField = 'title' | 'event_date' | 'totalRevenue' | 'registeredUsers'
type SortDirection = 'asc' | 'desc'

interface DropdownMenuProps {
  children: React.ReactNode
  position: { top: number; left: number }
}

const DropdownMenu: React.FC<DropdownMenuProps> = ({ children, position }) => {
  return createPortal(
    <div
      className="fixed w-48 bg-card border border-border rounded-lg shadow-lg z-50"
      style={{ top: `${position.top}px`, left: `${position.left}px` }}
    >
      {children}
    </div>,
    document.body
  )
}

const EventManagement: React.FC = () => {
  const [events, setEvents] = useState<EventWithStats[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [sortField, setSortField] = useState<SortField>('event_date')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 })

  useEffect(() => {
    loadEvents()
  }, [])

  // Handle outside clicks to close menu
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      // Check if click is outside the menu and button
      if (openMenuId && !target.closest('.dropdown-menu') && !target.closest('.kebab-button')) {
        setOpenMenuId(null)
      }
    }

    if (openMenuId) {
      document.addEventListener('mousedown', handleOutsideClick)
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [openMenuId])

  const loadEvents = async () => {
    try {
      setLoading(true)

      // Get events with related data
      const { data: eventsData, error: eventsError } = await supabase
        .from('secret_events')
        .select(`
          *,
          secret_orders (
            id,
            total_amount,
            status,
            user_id
          )
        `)
        .order('event_date', { ascending: false })

      if (eventsError) throw eventsError

      // Process events and calculate stats
      const eventsWithStats: EventWithStats[] = (eventsData || []).map(event => {
        const orders = event.secret_orders || []

        const totalOrders = orders.length
        const completedOrders = orders.filter((o: any) => o.status === 'completed').length
        const totalRevenue = orders
          .filter((o: any) => o.status !== 'cancelled')
          .reduce((sum: number, o: any) => sum + (o.total_amount || 0), 0)
        const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0
        const completionRate = totalOrders > 0 ? (completedOrders / totalOrders) * 100 : 0
        // Count unique users from orders only
        const uniqueUsers = new Set(orders.map((o: any) => o.user_id)).size

        return {
          ...event,
          totalOrders,
          totalRevenue,
          averageOrderValue,
          completionRate,
          registeredUsers: uniqueUsers
        }
      })

      setEvents(eventsWithStats)

      // Phase 3: Fetch lobster tail capacity for each event
      eventsWithStats.forEach(event => {
        if (event.max_lobster_tails !== null) {
          fetchEventCapacity(event.id)
        }
      })
    } catch (error) {
      console.error('Failed to load events:', error)
      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          visible={t.visible}
          type="error"
          title="Loading Failed"
          message="Could not load events"
        />
      ))
    } finally {
      setLoading(false)
    }
  }

  // Phase 3: Fetch lobster tail capacity from API endpoint
  const fetchEventCapacity = async (eventId: string) => {
    try {
      const response = await fetch(`/.netlify/functions/get-event-capacity?event_id=${eventId}`)
      if (!response.ok) {
        console.error('Capacity fetch failed:', await response.text())
        return
      }

      const data = await response.json()

      // Update event with capacity data
      setEvents(prev => prev.map(event =>
        event.id === eventId
          ? {
              ...event,
              current_tail_count: data.current_tail_count,
              capacity_percent: data.percent_used,
              capacity_loading: false
            }
          : event
      ))
    } catch (error) {
      console.error('Failed to fetch capacity for event', eventId, error)
    }
  }

  const updateEventStatus = async (eventId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('secret_events')
        .update({
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', eventId)

      if (error) throw error

      // Update local state
      setEvents(prev => prev.map(event =>
        event.id === eventId
          ? { ...event, status: newStatus }
          : event
      ))

      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          visible={t.visible}
          type="success"
          title="Status Updated"
          message={`Event status changed to ${newStatus}`}
        />
      ))
      setOpenMenuId(null)
    } catch (error) {
      console.error('Failed to update event status:', error)
      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          visible={t.visible}
          type="error"
          title="Update Failed"
          message="Could not update event status"
        />
      ))
    }
  }

  const deleteEvent = async (eventId: string) => {
    if (!confirm('Are you sure you want to delete this event? This action cannot be undone.')) {
      return
    }

    try {
      const { error } = await supabase
        .from('secret_events')
        .delete()
        .eq('id', eventId)

      if (error) throw error

      setEvents(prev => prev.filter(event => event.id !== eventId))
      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          visible={t.visible}
          type="success"
          title="Event Deleted"
          message="The event has been deleted"
        />
      ))
      setOpenMenuId(null)
    } catch (error) {
      console.error('Failed to delete event:', error)
      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          visible={t.visible}
          type="error"
          title="Delete Failed"
          message="Could not delete event"
        />
      ))
    }
  }

  const duplicateEvent = async (eventId: string) => {
    try {
      const event = events.find(e => e.id === eventId)
      if (!event) return

      const { data, error } = await supabase
        .from('secret_events')
        .insert({
          title: `${event.title} (Copy)`,
          description: event.description,
          location_name: event.location_name,
          address: event.address,
          status: 'draft',
          max_capacity: event.max_capacity,
          max_orders: event.max_orders,
          max_lobster_tails: event.max_lobster_tails
        })
        .select()
        .single()

      if (error) throw error

      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          visible={t.visible}
          type="success"
          title="Event Duplicated"
          message="The event has been copied"
        />
      ))
      loadEvents()
      setOpenMenuId(null)
    } catch (error) {
      console.error('Failed to duplicate event:', error)
      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          visible={t.visible}
          type="error"
          title="Duplicate Failed"
          message="Could not duplicate event"
        />
      ))
    }
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  const handleMenuToggle = (event: React.MouseEvent<HTMLButtonElement>, eventId: string) => {
    const buttonRect = event.currentTarget.getBoundingClientRect()
    setMenuPosition({
      top: buttonRect.bottom + 8,
      left: buttonRect.right - 192
    })
    setOpenMenuId(openMenuId === eventId ? null : eventId)
  }

  // Filter and sort events
  const filteredAndSortedEvents = events
    .filter(event => {
      const matchesSearch = event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           event.location_name.toLowerCase().includes(searchQuery.toLowerCase())
      const matchesStatus = statusFilter === 'all' || event.status === statusFilter
      return matchesSearch && matchesStatus
    })
    .sort((a, b) => {
      let aVal: any = a[sortField]
      let bVal: any = b[sortField]

      if (sortField === 'event_date') {
        aVal = new Date(aVal).getTime()
        bVal = new Date(bVal).getTime()
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1
      } else {
        return aVal < bVal ? 1 : -1
      }
    })

  // Calculate summary metrics using calculated status
  const totalRevenue = events.reduce((sum, e) => sum + e.totalRevenue, 0)
  const activeEvents = events.filter(e => {
    const status = getEventStatus(e as any)
    return status === 'Active' || status === 'In Progress'
  }).length
  const upcomingEvents = events.filter(e => {
    const status = getEventStatus(e as any)
    return status === 'Scheduled' || status === 'Enrollment Closed'
  }).length
  const totalOrders = events.reduce((sum, e) => sum + e.totalOrders, 0)

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-foreground flex items-center space-x-3">
            <Calendar size={32} className="text-primary" />
            <span>Event Management</span>
          </h2>
          <p className="text-muted-foreground mt-2">
            Create, manage and track SECRET events
          </p>
        </div>

        <Link to="/admin/events/new" className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-3 rounded-md flex items-center space-x-2 transition-colors font-medium shadow-sm">
          <Plus size={18} />
          <span>Create Event</span>
        </Link>
      </div>

      {/* Summary Metrics - Moved to top */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <DollarSign className="text-primary" size={28} />
            <span className="text-3xl font-bold text-foreground">${totalRevenue.toFixed(0)}</span>
          </div>
          <h3 className="font-semibold text-foreground text-sm">Total Revenue</h3>
          <p className="text-muted-foreground text-xs mt-1">From completed events</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <Calendar className="text-blue-600 dark:text-blue-400" size={28} />
            <span className="text-3xl font-bold text-foreground">{upcomingEvents}</span>
          </div>
          <h3 className="font-semibold text-foreground text-sm">Upcoming Events</h3>
          <p className="text-muted-foreground text-xs mt-1">Future scheduled</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <TrendingUp className="text-green-600 dark:text-green-400" size={28} />
            <span className="text-3xl font-bold text-foreground">{activeEvents}</span>
          </div>
          <h3 className="font-semibold text-foreground text-sm">Active Events</h3>
          <p className="text-muted-foreground text-xs mt-1">Currently open</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <Package className="text-orange-600 dark:text-orange-400" size={28} />
            <span className="text-3xl font-bold text-foreground">{totalOrders}</span>
          </div>
          <h3 className="font-semibold text-foreground text-sm">Total Orders</h3>
          <p className="text-muted-foreground text-xs mt-1">All time</p>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
            <input
              type="text"
              placeholder="Search events by name or location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-foreground placeholder:text-muted-foreground"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Filter className="text-muted-foreground" size={18} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-foreground"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Events Table */}
      {filteredAndSortedEvents.length > 0 ? (
        <div className="bg-card border border-border rounded-lg shadow-sm">
          <div className="overflow-x-auto overflow-y-visible rounded-lg">
            <table className="w-full relative">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="text-left px-6 py-4">
                    <button
                      onClick={() => handleSort('title')}
                      className="flex items-center space-x-1 font-semibold text-foreground hover:text-primary transition-colors"
                    >
                      <span>Event</span>
                      <ArrowUpDown size={14} />
                    </button>
                  </th>
                  <th className="text-left px-6 py-4">
                    <span className="font-semibold text-foreground">Status</span>
                  </th>
                  <th className="text-left px-6 py-4">
                    <button
                      onClick={() => handleSort('totalRevenue')}
                      className="flex items-center space-x-1 font-semibold text-foreground hover:text-primary transition-colors"
                    >
                      <span>Revenue</span>
                      <ArrowUpDown size={14} />
                    </button>
                  </th>
                  <th className="text-left px-6 py-4">
                    <button
                      onClick={() => handleSort('registeredUsers')}
                      className="flex items-center space-x-1 font-semibold text-foreground hover:text-primary transition-colors"
                    >
                      <span>Registered</span>
                      <ArrowUpDown size={14} />
                    </button>
                  </th>
                  <th className="text-left px-6 py-4">
                    <span className="font-semibold text-foreground">Capacity</span>
                  </th>
                  <th className="text-right px-6 py-4">
                    <span className="font-semibold text-foreground">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredAndSortedEvents.map((event) => (
                  <tr key={event.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-semibold text-foreground mb-1">{event.title}</div>
                        <div className="flex items-center space-x-3 text-sm text-muted-foreground">
                          <div className="flex items-center space-x-1">
                            <Calendar size={14} />
                            <span>
                              {new Date(event.event_date).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <MapPin size={14} />
                            <span>{event.location_name}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {(() => {
                        const calculatedStatus = getEventStatus(event as any)
                        return (
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(calculatedStatus)}`}>
                            {calculatedStatus}
                          </span>
                        )
                      })()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-semibold text-primary">
                        {formatPrice(event.totalRevenue * 100)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <Users size={16} className="text-muted-foreground" />
                        <span className="font-medium text-foreground">{event.registeredUsers}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-2 min-w-[200px]">
                        {/* Order Capacity */}
                        <div>
                          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                            <span>Orders</span>
                            <span>{event.current_orders}/{event.max_orders || '∞'}</span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-2">
                            <div
                              className={`h-2 rounded-full transition-all duration-300 ${
                                event.max_orders && event.current_orders >= event.max_orders
                                  ? 'bg-red-500'
                                  : event.max_orders && event.current_orders >= event.max_orders * 0.8
                                    ? 'bg-orange-500'
                                    : 'bg-green-500'
                              }`}
                              style={{
                                width: event.max_orders
                                  ? `${Math.min((event.current_orders / event.max_orders) * 100, 100)}%`
                                  : '0%'
                              }}
                            />
                          </div>
                        </div>

                        {/* Lobster Tail Capacity */}
                        {event.max_lobster_tails !== null && event.max_lobster_tails > 0 && (
                          <div>
                            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                              <span>🦞 Tails</span>
                              <span>
                                {event.current_tail_count !== undefined
                                  ? `${event.current_tail_count}/${event.max_lobster_tails}`
                                  : `0/${event.max_lobster_tails}`}
                              </span>
                            </div>
                            {event.capacity_percent !== undefined && (
                              <div className="w-full bg-muted rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full transition-all duration-300 ${
                                    event.capacity_percent >= 100
                                      ? 'bg-red-500 dark:bg-red-400'
                                      : event.capacity_percent >= 75
                                        ? 'bg-orange-500 dark:bg-orange-400'
                                        : 'bg-green-500 dark:bg-green-400'
                                  }`}
                                  style={{ width: `${Math.min(event.capacity_percent, 100)}%` }}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="relative inline-block">
                        <button
                          onClick={(e) => handleMenuToggle(e, event.id)}
                          className="kebab-button p-2 hover:bg-muted rounded-md transition-colors"
                        >
                          <MoreVertical size={18} className="text-foreground" />
                        </button>

                        {/* Kebab Menu Dropdown */}
                        {openMenuId === event.id && (
                          <DropdownMenu position={menuPosition}>
                            <div className="dropdown-menu">
                              <Link
                                to={`/admin/events/edit/${event.id}`}
                                className="flex items-center space-x-3 px-4 py-3 hover:bg-muted/50 transition-colors border-b border-border"
                                onClick={() => setOpenMenuId(null)}
                              >
                                <Edit size={16} className="text-blue-600 dark:text-blue-400" />
                                <span className="text-sm font-medium text-foreground">Edit</span>
                              </Link>

                              <Link
                                to={`/events/${event.id}`}
                                className="flex items-center space-x-3 px-4 py-3 hover:bg-muted/50 transition-colors border-b border-border"
                                onClick={() => setOpenMenuId(null)}
                              >
                                <Eye size={16} className="text-purple-600 dark:text-purple-400" />
                                <span className="text-sm font-medium text-foreground">View Stats</span>
                              </Link>

                              <button
                                onClick={() => duplicateEvent(event.id)}
                                className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-muted/50 transition-colors border-b border-border"
                              >
                                <Copy size={16} className="text-green-600 dark:text-green-400" />
                                <span className="text-sm font-medium text-foreground">Duplicate</span>
                              </button>

                              <button
                                onClick={() => deleteEvent(event.id)}
                                className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-red-500/10 dark:hover:bg-red-400/10 transition-colors"
                              >
                                <Trash2 size={16} className="text-red-600 dark:text-red-400" />
                                <span className="text-sm font-medium text-red-600 dark:text-red-400">Delete</span>
                              </button>
                            </div>
                          </DropdownMenu>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg p-16 text-center shadow-sm">
          <Calendar size={64} className="text-muted-foreground/50 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-foreground mb-2">
            {searchQuery || statusFilter !== 'all' ? 'No events found' : 'No Events Created'}
          </h3>
          <p className="text-muted-foreground mb-6">
            {searchQuery || statusFilter !== 'all'
              ? 'Try adjusting your search or filters.'
              : 'Create your first SECRET event to start taking pre-orders.'}
          </p>
          {!searchQuery && statusFilter === 'all' && (
            <Link to="/admin/events/new" className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-3 rounded-md inline-flex items-center space-x-2 transition-colors font-medium shadow-sm">
              <Plus size={18} />
              <span>Create First Event</span>
            </Link>
          )}
        </div>
      )}
    </div>
  )
}

export default EventManagement
